import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.recalculate()
  }

  recalculate() {
    const studentAccommodationMealPrice = this.numberFromInput("student_accommodation_meal_price")
    const otherCollectedStudentFees = this.numberFromInput("other_collected_student_fees")
    const staffAccommodationMealCostsTotal = this.numberFromInput("staff_accommodation_meal_costs_total")
    const otherServicesPurchasesTotal = this.numberFromInput("other_services_purchases_total")
    const travelCostsTotal = this.numberFromInput("travel_costs_total")
    const dppCosts = this.numberFromInput("dpp_costs")
    const expectedStudentCount = this.numberFromInput("expected_student_count")
    const nightCount = this.numberFromDataset("programDateRangesNightCount")

    const accommodationAndMealsPerStudent = this.multiply(studentAccommodationMealPrice, nightCount)
    const totalPerStudent = this.sum(accommodationAndMealsPerStudent, otherCollectedStudentFees)
    const studentCostsTotal = this.multiply(accommodationAndMealsPerStudent, expectedStudentCount)
    const teacherCostsTotal = this.sum(
      travelCostsTotal,
      staffAccommodationMealCostsTotal,
      dppCosts,
      otherServicesPurchasesTotal
    )
    const expectedRevenue = studentCostsTotal
    const totalCosts = this.sum(studentCostsTotal, teacherCostsTotal)
    const pricePerStudent = this.sum(accommodationAndMealsPerStudent, otherCollectedStudentFees)
    const facultySubsidy = this.subtract(totalCosts, expectedRevenue)

    this.writeOutput("total_costs", totalCosts)
    this.writeOutput("student_costs_total", studentCostsTotal)
    this.writeOutput("teacher_costs_total", teacherCostsTotal)
    this.writeOutput("expected_revenue", expectedRevenue)
    this.writeOutput("price_per_student", pricePerStudent)
    this.writeOutput("total_per_student", totalPerStudent)
    this.writeOutput("faculty_subsidy", facultySubsidy)
  }

  numberFromInput(key) {
    const element = this.element.querySelector(`[data-calculation-input="${key}"]`)
    if (!element) return null

    const normalizedValue = element.value.trim().replace(",", ".")
    if (normalizedValue.length === 0) return null

    const value = Number(normalizedValue)
    return Number.isFinite(value) ? value : null
  }

  writeOutput(key, value) {
    const elements = this.element.querySelectorAll(`[data-calculation-output="${key}"]`)
    if (elements.length === 0) return

    const rawValue = typeof value === "string" ? value : value === null ? "" : this.formatNumber(value)

    elements.forEach((element) => {
      if ("value" in element) {
        element.value = rawValue
      } else {
        element.textContent = this.formatDisplayValue(value, element.dataset.calculationOutputFormat)
      }
    })
  }

  numberFromDataset(key) {
    const value = this.element.dataset[key]
    if (!value) return null

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  sum(...values) {
    if (values.every((value) => value === null)) return null

    return values.reduce((total, value) => total + (value ?? 0), 0)
  }

  multiply(...values) {
    if (values.some((value) => value === null)) return null

    return values.reduce((total, value) => total * value, 1)
  }

  subtract(first, second) {
    if (first === null || second === null) return null

    return first - second
  }

  formatNumber(value) {
    return new Intl.NumberFormat("cs-CZ", {
      useGrouping: false,
      maximumFractionDigits: 0
    }).format(Math.round(value))
  }

  formatDisplayValue(value, format) {
    if (value === null || value === "") return "—"
    if (typeof value === "string") return value
    if (format === "currency") return `${this.formatGroupedNumber(value)} Kč`

    return this.formatGroupedNumber(value)
  }

  formatGroupedNumber(value) {
    return new Intl.NumberFormat("cs-CZ", {
      useGrouping: true,
      maximumFractionDigits: 0
    }).format(Math.round(value))
  }
}
